<template>
  <div class="min-h-screen w-full min-w-0 bg-[#f8f9fa] dark:bg-gray-950">
    <GovUtilityBar fixed @utility-action="goToLogin" />

    <div class="w-full min-w-0 max-w-full flex justify-center px-3 sm:px-4 pt-20 sm:pt-24 pb-10">
      <div class="w-full min-w-0 max-w-md">
        <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-8">
          <h1 class="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Forgot Password</h1>
          <p class="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
            Enter the email on your TMS account. If it exists, we will send a reset link (valid for 15 minutes).
          </p>

          <form class="mt-6 space-y-4" @submit.prevent="submit">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="email">Email Address</label>
              <input
                id="email"
                name="email"
                v-model="email"
                type="email"
                required
                autocomplete="email"
                class="form-input w-full min-h-[44px] text-base sm:text-sm"
                placeholder="Enter your email address"
              />
            </div>

            <div
              v-if="message"
              class="text-sm p-3 rounded-md"
              :class="success ? 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20' : 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20'"
            >
              {{ message }}
            </div>

            <div
              v-if="devResetUrl"
              class="text-sm p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
            >
              <p class="font-medium">Development mode</p>
              <p class="mt-1 text-xs sm:text-sm opacity-90">Email is not sent locally. Use this link to reset your password:</p>
              <a
                :href="devResetUrl"
                class="mt-2 block break-all text-primary-600 dark:text-primary-400 hover:underline font-medium"
              >
                Open reset password page
              </a>
            </div>

            <button
              type="submit"
              :disabled="loading"
              class="btn-primary w-full min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed py-3 touch-manipulation"
            >
              <span v-if="loading" class="inline-flex items-center justify-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </span>
              <span v-else>Send reset link</span>
            </button>

            <router-link to="/login" class="block text-center text-xs sm:text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors">
              Back to login
            </router-link>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import GovUtilityBar from '../components/GovUtilityBar.vue'
import { authService } from '../services/auth'

const router = useRouter()
const email = ref('')
const loading = ref(false)
const message = ref('')
const success = ref(false)
const devResetUrl = ref('')

function goToLogin() {
  router.push('/login')
}

async function submit() {
  loading.value = true
  message.value = ''
  success.value = false
  devResetUrl.value = ''

  try {
    const result = await authService.requestPasswordReset(email.value.trim())
    success.value = result.success
    message.value =
      result.message ||
      (result.success
        ? 'If an account with this email exists, a reset link has been sent.'
        : 'Failed to send reset link.')
    devResetUrl.value = result.devResetUrl || ''
  } catch {
    message.value = 'Failed to send reset link.'
    success.value = false
  } finally {
    loading.value = false
  }
}
</script>
